/**
 * Light mode: RiskLo's gain / loss text colors were picked for dark backgrounds
 * (#4ade80, #22c55e, #f87171 …) and read too light on white. This adds an
 * override sheet, applied only under body.light-mode, that swaps those greens
 * and reds for darker ones on text. Backgrounds, borders and chart fills are
 * left alone (a soft green area under a line is fine on white).
 *
 * Covers both the app's stylesheets (read once, and again when lazy pages add
 * theirs) and colors set inline from code (style="color: …").
 */

const STYLE_ID = 'risklo-light-mode-colors';
const GREEN = '#15803d';
const RED = '#dc2626';

// Bright greens / reds used for gain and loss text, as rgb() triples.
const GREENS = ['74, 222, 128', '34, 197, 94', '52, 211, 153', '16, 185, 129', '134, 239, 172', '110, 231, 183', '0, 200, 83', '0, 230, 118'];
const REDS = ['248, 113, 113', '239, 68, 68', '252, 165, 165', '251, 113, 133', '244, 63, 94', '255, 82, 82'];
const HEX = {
  '#4ade80': GREEN, '#22c55e': GREEN, '#34d399': GREEN, '#10b981': GREEN, '#86efac': GREEN, '#6ee7b7': GREEN,
  '#f87171': RED, '#ef4444': RED, '#fca5a5': RED, '#fb7185': RED, '#f43f5e': RED,
};
const TEXT_PROPS = ['color', '-webkit-text-fill-color'];

/** The darker light-mode color for a value, or null when it isn't one of ours. */
export function darkerFor(value) {
  const v = String(value || '').trim().toLowerCase();
  if (HEX[v]) return HEX[v];
  const m = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (!m) return null;
  if (m[4] != null && Number(m[4]) < 0.6) return null; // faint tints aren't text colors
  const triple = `${m[1]}, ${m[2]}, ${m[3]}`;
  if (GREENS.includes(triple)) return GREEN;
  if (REDS.includes(triple)) return RED;
  return null;
}

function splitSelectors(text) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (const ch of text) {
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    if (ch === ',' && depth === 0) { out.push(cur.trim()); cur = ''; } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

/** Scope a selector to light mode (already light-mode rules stay as written; this sheet comes last). */
function scope(sel) {
  if (sel.includes('light-mode')) return sel;
  if (/^(:root|html)\b/.test(sel)) return null;
  if (/^body\b/.test(sel)) return sel.replace(/^body/, 'body.light-mode');
  return `body.light-mode ${sel}`;
}

function rulesFrom(list, out) {
  for (const rule of Array.from(list || [])) {
    if (rule.type === 1) {
      const decls = [];
      TEXT_PROPS.forEach((prop) => {
        const value = rule.style.getPropertyValue(prop);
        const next = value ? darkerFor(value) : null;
        if (next) decls.push(`${prop}: ${next}${rule.style.getPropertyPriority(prop) ? ' !important' : ''};`);
      });
      if (decls.length) {
        const sels = splitSelectors(rule.selectorText || '').map(scope).filter(Boolean);
        if (sels.length) out.push(`${sels.join(', ')} { ${decls.join(' ')} }`);
      }
    } else if (rule.type === 4 && rule.cssRules) {
      const inner = [];
      rulesFrom(rule.cssRules, inner);
      if (inner.length) out.push(`@media ${rule.conditionText || rule.media?.mediaText} { ${inner.join('\n')} }`);
    } else if (rule.cssRules) {
      rulesFrom(rule.cssRules, out);
    }
  }
}

/** Inline colors from code: style="color: rgb(…)" (matched as the color property, not background-color). */
function inlineRules() {
  const one = (triple, to) => {
    const v = `color: rgb(${triple})`;
    return `body.light-mode [style^="${v}"], body.light-mode [style*="; ${v}"] { color: ${to} !important; }`;
  };
  return [...GREENS.map((t) => one(t, GREEN)), ...REDS.map((t) => one(t, RED))].join('\n');
}

function build() {
  const out = [];
  for (const sheet of Array.from(document.styleSheets)) {
    if (sheet.ownerNode?.id === STYLE_ID) continue;
    let rules;
    try { rules = sheet.cssRules; } catch (_e) { continue; } // cross-origin (fonts, widgets)
    rulesFrom(rules, out);
  }
  out.push(inlineRules());
  return out.join('\n');
}

let installed = false;
let timer = null;

function render() {
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = build();
  if (el !== document.head.lastElementChild) document.head.appendChild(el);
}

/** Install once at startup; it only has an effect while body has .light-mode. */
export function installLightModeColors() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  render();
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((n) => (n.tagName === 'STYLE' || n.tagName === 'LINK') && n.id !== STYLE_ID))) {
        clearTimeout(timer);
        timer = setTimeout(render, 150);
      }
    }).observe(document.head, { childList: true });
  }
}

export const _internal = { scope, inlineRules, GREEN, RED };
